// MAJLIS-164 (rulings 1 and 2): one catalogue per interface language, behind ONE typed table. The interface languages
// are the "I read in" languages (READ_LANGUAGES) less Cantonese, which reads Traditional Chinese (uiLangFor). English is
// the key source and sits in the main bundle; every other language is a dynamic import, so the build makes it its own
// chunk and a phone fetches only the one it reads. PR 1 shipped en and ar; PR 2 adds the 28 generated catalogues, each
// wired by its own `() => import("./<tag>")` line once the consultant's verdict on it is PASS (VERDICTS below).
import { en, type StringKey } from "./en";

/** Every interface language: READ_LANGUAGES' tags without yue (catalogues.test.ts holds the two lists together). */
export const UI_LANGS = [
  "ar", "en", "zh-Hans", "zh-Hant", "de", "fr", "es", "pt", "id", "it", "ko", "ru", "th", "vi", "ja",
  "tr", "hi", "ms", "nl", "sv", "da", "fi", "pl", "cs", "fil", "fa", "el", "hu", "mk", "ro",
] as const;
export type UiLang = (typeof UI_LANGS)[number];

export type Catalogue = Record<StringKey, string>;
export type CatalogueLoader = () => Promise<Catalogue>;

/** tag -> its catalogue: a lazy import (its own chunk), or "en" while the language has no catalogue of its own. */
export type CatalogueTable = { readonly [L in UiLang]: CatalogueLoader | "en" };

/** MAJLIS-164 (the consultant, 2026-10-04): a language's catalogue is WIRED - its line below turned from "en" into a
 * lazy import - only after the consultant's verdict on that language, recorded here in the same change. The generator
 * writes catalogues/<tag>.ts and never wires it; PR 2 ships every generated file and wires only the passed languages.
 * catalogues.test.tsx holds the wired set equal to this one, and the build's chunk check (scripts/check-chunks.mjs)
 * proves no unwired catalogue reaches any chunk. */
export const VERDICTS: Partial<Record<UiLang, string>> = {
  ar: "the consultant's formal Arabic (MAJLIS-163, 2026-10-04): the reference catalogue, never generated",
  "zh-Hans": "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/zh-Hans.json)",
  "zh-Hant": "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/zh-Hant.json)",
  de: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): regenerated under its glossary rule and required terms, no change",
  fr: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/fr.json)",
  es: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/es.json)",
  pt: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): no override",
  id: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): regenerated under its glossary rule and required terms; with his override of the short Auto-play word (web/tools/i18n/overrides/id.json, Addendum 3)",
  it: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/it.json); regenerated first",
  ko: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/ko.json)",
  ru: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/ru.json); regenerated first",
  th: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/th.json; Addendum 3, the short Auto-play word)",
  vi: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/vi.json)",
  ja: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/ja.json); regenerated first",
  tr: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/tr.json)",
  hi: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): no override",
  ms: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/ms.json)",
  nl: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/nl.json)",
  sv: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/sv.json)",
  da: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/da.json)",
  fi: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/fi.json)",
  pl: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/pl.json)",
  cs: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/cs.json)",
  fil: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/fil.json)",
  fa: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/fa.json); regenerated first",
  el: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/el.json); regenerated first; Addendum 3 closed its last item (recordHostOnly «Καταγραφή»)",
  hu: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/hu.json); regenerated first",
  mk: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/mk.json)",
  ro: "PASS, the consultant's verdicts of 2026-10-04 (docs/i18n/verdicts-2026-10-04.md): with his overrides (web/tools/i18n/overrides/ro.json)",
};

export const CATALOGUES: CatalogueTable = {
  en: "en",
  ar: () => import("./ar").then((m) => m.ar),
  "zh-Hans": () => import("./zh-Hans").then((m) => m.zhHans),
  "zh-Hant": () => import("./zh-Hant").then((m) => m.zhHant),
  de: () => import("./de").then((m) => m.de),
  fr: () => import("./fr").then((m) => m.fr),
  es: () => import("./es").then((m) => m.es),
  pt: () => import("./pt").then((m) => m.pt),
  id: () => import("./id").then((m) => m.id),
  it: () => import("./it").then((m) => m.it),
  ko: () => import("./ko").then((m) => m.ko),
  ru: () => import("./ru").then((m) => m.ru),
  th: () => import("./th").then((m) => m.th),
  vi: () => import("./vi").then((m) => m.vi),
  ja: () => import("./ja").then((m) => m.ja),
  tr: () => import("./tr").then((m) => m.tr),
  hi: () => import("./hi").then((m) => m.hi),
  ms: () => import("./ms").then((m) => m.ms),
  nl: () => import("./nl").then((m) => m.nl),
  sv: () => import("./sv").then((m) => m.sv),
  da: () => import("./da").then((m) => m.da),
  fi: () => import("./fi").then((m) => m.fi),
  pl: () => import("./pl").then((m) => m.pl),
  cs: () => import("./cs").then((m) => m.cs),
  fil: () => import("./fil").then((m) => m.fil),
  fa: () => import("./fa").then((m) => m.fa),
  el: () => import("./el").then((m) => m.el),
  hu: () => import("./hu").then((m) => m.hu),
  mk: () => import("./mk").then((m) => m.mk),
  ro: () => import("./ro").then((m) => m.ro),
};

/** The language whose words `lang`'s interface shows today: `lang` itself once it has a catalogue, else English. Plural
 * categories follow THIS (the forms come from this catalogue), never the bare tag. */
export function catalogueLangOf(lang: UiLang, table: CatalogueTable = CATALOGUES): UiLang {
  return table[lang] === "en" ? "en" : lang;
}

export interface CatalogueStore {
  /** The language whose words `lang`'s interface draws from this store's table (catalogueLangOf). */
  wordsOf(lang: UiLang): UiLang;
  /** The catalogue if it is in hand now: English and every tag mapped to it always are; a lazy one once loaded. */
  ready(lang: UiLang): Catalogue | undefined;
  /** Fetch a lazy catalogue (once; a failed fetch is forgotten, so the next call tries again). */
  load(lang: UiLang): Promise<Catalogue>;
}

export function createCatalogueStore(table: CatalogueTable = CATALOGUES): CatalogueStore {
  const loaded = new Map<UiLang, Catalogue>();
  const pending = new Map<UiLang, Promise<Catalogue>>();
  return {
    wordsOf: (lang) => catalogueLangOf(lang, table),
    ready(lang) {
      const entry = table[lang];
      return entry === "en" ? en : loaded.get(lang);
    },
    load(lang) {
      const entry = table[lang];
      if (entry === "en") return Promise.resolve(en);
      const have = loaded.get(lang);
      if (have) return Promise.resolve(have);
      let p = pending.get(lang);
      if (!p) {
        p = entry().then(
          (c) => {
            loaded.set(lang, c);
            pending.delete(lang);
            return c;
          },
          (err: unknown) => {
            pending.delete(lang);
            throw err;
          },
        );
        pending.set(lang, p);
      }
      return p;
    },
  };
}

/** The app's one store. */
export const catalogues = createCatalogueStore();
